import { useMemo, useState } from "react";
import { animes, categories } from "@/mocks/animes";

interface AnimeGridProps {
  keyword: string;
}

export default function AnimeGrid({ keyword }: AnimeGridProps) {
  const [active, setActive] = useState<string>("全部");

  const list = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return animes.filter((a) => {
      const catOk = active === "全部" || a.category === active;
      const kwOk =
        !kw ||
        a.title.toLowerCase().includes(kw) ||
        a.originalTitle.toLowerCase().includes(kw) ||
        a.tags.some((t) => t.toLowerCase().includes(kw));
      return catOk && kwOk;
    });
  }, [active, keyword]);

  return (
    <section id="list" className="border-y border-background-200/70 bg-background-100/70">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
        <div className="mb-6">
          <span className="text-xs font-medium tracking-widest text-primary-600 uppercase">
            番剧合集
          </span>
          <h2 className="mt-2 font-heading text-2xl text-foreground-950 md:text-3xl">
            按分类挑一部好番
          </h2>
          <p className="mt-2 text-sm text-foreground-600">
            治愈系、日常系、温馨番剧、萌系动画，总有一部合你当下的心情。
          </p>
        </div>

        <div className="mb-8 flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setActive(c)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                active === c
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-50 text-foreground-700 hover:bg-primary-100/70 hover:text-primary-700"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {list.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <i className="ri-search-line text-3xl text-foreground-300"></i>
            <p className="mt-3 text-sm text-foreground-600">
              没有找到「{keyword}」相关的番剧，换个关键词试试吧。
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {list.map((a) => (
              <article
                key={a.id}
                className="group flex flex-col overflow-hidden rounded-lg bg-background-50 border border-background-200/70"
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={a.image}
                    alt={`${a.title} 动漫封面海报`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-foreground-950/50 px-2.5 py-0.5 text-xs text-background-50 backdrop-blur">
                    {a.category}
                  </span>
                  <span className="absolute right-3 top-3 rounded-full bg-primary-500 px-2.5 py-0.5 text-xs text-background-50">
                    {a.rating}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <div className="flex items-baseline justify-between gap-2">
                    <h3 className="font-heading text-base text-foreground-950">{a.title}</h3>
                    <span className="whitespace-nowrap text-xs text-foreground-500">{a.episodes}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {a.tags.map((t) => (
                      <span
                        key={t}
                        className="whitespace-nowrap rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  <p className="mt-3 flex-1 line-clamp-2 text-xs leading-relaxed text-foreground-600">
                    {a.summary}
                  </p>
                  <p className="mt-3 flex items-center gap-1.5 border-t border-background-200/70 pt-3 text-xs text-accent-700">
                    <i className="ri-eye-2-line"></i>
                    <span className="line-clamp-1">{a.highlights}</span>
                  </p>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}